import { useEffect, useMemo, useRef } from 'react'

export type LocalDistanceOption = 'city' | 5 | 10 | 25 | 50 | 100
export type LocalTargetMode = 'near_me' | 'profile_city' | 'custom_city'

type Props = {
  enabled: boolean
  onEnabledChange: (next: boolean) => void
  distance: LocalDistanceOption
  onDistanceChange: (next: LocalDistanceOption) => void
  targetMode: LocalTargetMode
  onTargetModeChange: (next: LocalTargetMode) => void
  hasCoordinates: boolean
  onUseLocation: () => void
  cityLabel: string
  onCityLabelChange: (next: string) => void
  profileCityLabel: string
  previewLocationLabel: string
  forceFocusCityInput?: boolean
}

export default function LocalPublishOptions({
  enabled,
  onEnabledChange,
  distance,
  onDistanceChange,
  targetMode,
  onTargetModeChange,
  hasCoordinates,
  onUseLocation,
  cityLabel,
  onCityLabelChange,
  profileCityLabel,
  previewLocationLabel,
  forceFocusCityInput = false,
}: Props) {
  const cityInputRef = useRef<HTMLInputElement | null>(null)
  const preview = useMemo(() => {
    const label = (previewLocationLabel || '').trim()
    if (targetMode === 'profile_city') {
      if (!label) return 'Shown in your city'
      return distance === 'city' ? `Shown in ${label}` : `Shown within ${distance} mi of ${label}`
    }
    if (targetMode === 'custom_city') {
      if (!label) return 'Shown in your city'
      return distance === 'city' ? `Shown in ${label}` : `Shown within ${distance} mi of ${label}`
    }
    if (distance === 'city') return 'Shown near your location'
    return `Shown within ${distance} mi of your location`
  }, [distance, previewLocationLabel, targetMode])

  useEffect(() => {
    if (!enabled || targetMode !== 'custom_city' || !forceFocusCityInput) return
    cityInputRef.current?.focus()
  }, [enabled, targetMode, forceFocusCityInput])

  return (
    <div className="rounded-xl border border-white/10 p-3">
      <div className="text-sm font-semibold">Local</div>
      <div className="mt-1 text-xs text-white/60">Show this Pixe to people near your location.</div>
      <label className="mt-2 inline-flex items-center gap-2 text-sm text-white/85">
        <input type="checkbox" checked={enabled} onChange={(e) => onEnabledChange(Boolean(e.target.checked))} />
        <span>Mark as Local</span>
      </label>

      {enabled ? (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <label className="text-xs text-white/65">Distance</label>
            <select
              aria-label="Distance"
              value={String(distance)}
              onChange={(e) => {
                const raw = e.target.value
                onDistanceChange(raw === 'city' ? 'city' : (Number(raw) as LocalDistanceOption))
              }}
              className="h-7 min-w-[130px] rounded-md border border-white/10 bg-black px-2 text-sm text-white outline-none focus:border-white/20"
            >
              <option value="city">City</option>
              <option value="5">5 mi</option>
              <option value="10">10 mi</option>
              <option value="25">25 mi</option>
              <option value="50">50 mi</option>
              <option value="100">100 mi</option>
            </select>

            <label className="text-xs text-white/65">Target</label>
            <select
              aria-label="Target"
              value={targetMode}
              onChange={(e) => onTargetModeChange(e.target.value as LocalTargetMode)}
              className="h-7 min-w-[140px] rounded-md border border-white/10 bg-black px-2 text-sm text-white outline-none focus:border-white/20"
            >
              <option value="near_me">Near me</option>
              <option value="profile_city">Profile city</option>
              <option value="custom_city">Custom city</option>
            </select>

            {targetMode === 'near_me' && !hasCoordinates ? (
              <button
                type="button"
                onClick={onUseLocation}
                className="h-7 rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white/90 hover:bg-white/10"
              >
                Use location
              </button>
            ) : null}

            {targetMode !== 'near_me' ? (
              <input
                ref={cityInputRef}
                autoFocus={targetMode === 'custom_city'}
                value={targetMode === 'profile_city' ? (cityLabel || profileCityLabel || previewLocationLabel || '') : cityLabel}
                onChange={(e) => onCityLabelChange(e.target.value)}
                placeholder="San Diego, CA"
                className="h-7 min-w-[160px] rounded-md border border-white/10 bg-black px-2 text-sm text-white outline-none focus:border-white/20"
              />
            ) : null}
          </div>
          <div className="mt-1 text-xs text-white/65">{preview}</div>
        </>
      ) : null}
    </div>
  )
}
